import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { MultiContentStageSchemeDefinition, MultiContentStageSchemeName } from "./constants/multi-content-stage-types";
import {
  MULTI_CONTENT_STAGE_SCHEME_CONFIG,
  MULTI_CONTENT_STAGE_SCHEME_MAP
} from "./constants/multi-content-stage-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isMobile, isTablet } from "../../utils/screen-helper";
import { isFirefox } from "../../utils/device-helper";
import { MultiContentStageSchemeConfiguration } from "../../constants/colors";
import { getNestedChildren } from "../../utils/dom-helper";
import { KeyIds } from "../../constants/keys";
import { detectSlotChanges } from "../../utils/slot-helper";
import { MultiContentStageColorBackgrounds } from "../../constants/global-constants";

import { BackgroundColor } from "../background/constants/background-types";
import { uniqueId } from "../../utils/unique-id";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot [static-background-image] - holds one eon-ui-image as static background.
 */
@Component({
  tag: "eon-ui-multi-content-stage",
  styleUrl: "multi-content-stage.scss",
  shadow: true
})
export class MultiContentStage
  implements ColorableComponent<MultiContentStageSchemeConfiguration<MultiContentStageSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: MultiContentStageSchemeConfiguration<MultiContentStageSchemeDefinition> = MULTI_CONTENT_STAGE_SCHEME_CONFIG;
  stagesWrapper!: HTMLElement;
  wrapper!: HTMLElement;
  contentWrapper!: HTMLElement;
  contentWrapperInner!: HTMLElement;
  wrapperFrame!: HTMLElement;
  autoslider: any;
  initialTimeout: any;
  staticBackground: HTMLElement;
  schemeMap = MULTI_CONTENT_STAGE_SCHEME_MAP;
  carousel: HTMLEonUiCarouselElement;
  resizeTimeout: NodeJS.Timeout;
  resizeThrottleMs = 300;
  isTextOnBackground: boolean = false;
  mscItemPositionBottom: number;
  private overflowTimeout: any;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: MultiContentStageSchemeDefinition;

  /**
   * Collection on stages
   */
  @State() stages: Element[] = [];

  /**
   * Width of one stage tile
   */
  @State() stagesWidth: number;

  /**
   * Visibility of the left slider control button
   */
  @State() leftControlVisible: boolean;

  /**
   * Visibility of the right slider control button
   */
  @State() rightControlVisible: boolean;

  /**
   * Position of slider movable wrapper relative to container
   */
  @State() sliderPosition: number = 0;

  /**
   * Number of available stages
   */
  @State() stagesCount: number;

  /**
   * Number of visible stage tiles per view, 4 per default
   */
  @State() slidesPerView: number = 4;

  /**
   * Show controls of carousel or not
   */
  @State() showControls: boolean = true;

  /**
   * Absolute width of movable stages wrapper
   */
  @State() stagesWrapperWidth: string;

  /**
   * Detects big screens to show oversize wrapper
   */
  @State() wrapperOversize: boolean;

  /**
   * Height of the background image
   */
  @State() backgroundSize: number;

  /**
   * Turn on/off slider autochange
   */
  @State() internalAutoSlider: boolean = false;

  /**
   * Fast forward animation
   */
  @State() fastForwardAnimation: boolean = false;

  /**
   * Size of the promo-flag (if found)
   */
  @State() promoFlagSize: "small" | "normal";

  /**
   * Is slider activated or not
   */
  @Prop({ mutable: true }) isSlider: boolean;

  /**
   * Slider autochange timer
   */
  @Prop() sliderTimer: number;

  /**
   * Turn on/off slider autochange
   */
  @Prop({ mutable: true }) autoSlider: boolean;

  /**
   * Blur background
   */
  @Prop() blurBackground: boolean;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: MultiContentStageSchemeName;

  /**
   * Sets this to a valid zero-based index of a stage to select it.
   */
  @Prop({ reflect: true, mutable: true }) activeStageIndex: number = 0;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Colorized Background
   */
  @Prop({ reflect: true, mutable: true }) backgroundColor: MultiContentStageColorBackgrounds;

  @Prop() bottomAlignment: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiMultiContentStageElement;

  /**
   * Stores the name of current viewport
   */
  @State() currentScreenSize: number;

  /**
   * Raised when the mcs has been loaded.
   */
  @Event() mcsLoad: EventEmitter;

  /**
   * Raised when one of the mcs items has been clicked or auto play is interrupted.
   */
  @Event() mcsItemClick: EventEmitter<number>;

  @State() overflowHidden: boolean = false;

  @State() headingText: HTMLElement;
  @State() subHeadingText: HTMLElement;
  @State() buttonWrapper: HTMLElement;

  /**
   * Use this option to reduce the header's height, particularly when using a teaser without a image.
   */
  @Prop() reduceHeaderHeight: boolean = false;

  /**
   * A teaser without image.
   */
  @State() noImage: boolean = false;

  /**
   * index of keyboard-activated tab, managed by onFocus / onBlur
   */
  @State() activeTabIndex: number = -1;

  private tabIdPrefix: string;

  private ignoreNextFocus: boolean = false;

  componentWillLoad() {
    this.applyScheme();
    this.stages = Array.from(this.hostElement.getElementsByTagName("EON-UI-MULTI-CONTENT-STAGE-ITEM"));
    this.stagesCount = this.stages.length;
    this.staticBackground = this.hostElement.querySelector('[slot="static-background-image"]');

    this.headingText = this.hostElement.querySelector('[slot="heading"]');
    this.subHeadingText = this.hostElement.querySelector('[slot="subheading"]');
    this.buttonWrapper = this.hostElement.querySelector('[slot="button-wrapper"]');

    if (this.headingText || this.subHeadingText || this.buttonWrapper) {
      this.isTextOnBackground = true;
    }
    this.getPromoFlagSize();
    this.stageWithoutImage();
    const mcsId = uniqueId();
    this.tabIdPrefix = `mcs-${mcsId}-`;
  }

  componentDidLoad() {
    this.setCssVariables();
    this.setViewportSpecificSettings();
    this.handleAutoSlider();
    this.resizeHandler();
    this.imageVisibility();
    this.selectStage();
    this.setMscItemPositionBottom();

    detectSlotChanges(
      () => {
        setTimeout(() => {
          this.setContentWrapperHeight();
        }, 0);
      },
      this.hostElement,
      { childList: true, subtree: true, attributes: true }
    );

    this.mcsLoad.emit();
  }

  setMscItemPositionBottom() {
    if (!this.bottomAlignment) {
      return;
    }

    if (isMobile()) {
      if (!this.isTextOnBackground) {
        this.mscItemPositionBottom = 0;
      } else {
        this.mscItemPositionBottom = 115;
      }
    } else {
      this.mscItemPositionBottom =
        (this.hostElement.shadowRoot.querySelector(".eonui-mcs-item") as HTMLElement).offsetHeight / 2 + 100;
    }
  }

  imageVisibility() {
    const isImageAvailable = Array.from(getNestedChildren(this.hostElement, "eon-ui-image"));
    isImageAvailable.forEach((imageItem) => {
      if (imageItem.slot === "stage-item-image" || imageItem.slot === "stage-item-background") {
        imageItem.addEventListener("imageSrcLoad", () => {
          this.resizeHandler();
        });
      }
    });
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.hostElement.style.setProperty("--animationTime", `${this.sliderTimer}ms`);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("activeStageIndex")
  selectStage(_newValue?) {
    if (this.activeStageIndex < 0) this.activeStageIndex = this.stagesCount - 1;
    if (this.activeStageIndex >= this.stagesCount) this.activeStageIndex = 0;

    this.stages.forEach((stage, index) =>
      stage.setAttribute("is-active", (index === this.activeStageIndex).toString())
    );

    if (this.overflowTimeout) clearTimeout(this.overflowTimeout);
    this.overflowHidden = true;
    this.setContentWrapperHeight();
    this.overflowTimeout = setTimeout(() => {
      this.overflowHidden = false;
    }, 500); // 250ms + 100ms delay is aligned with the css transition time
  }

  private getPromoFlagSize() {
    const slottedPromoFlags = Array.from(getNestedChildren(this.hostElement, "eon-ui-promo-flag"));

    if (slottedPromoFlags.length) {
      let smallCount = 0;

      slottedPromoFlags.forEach((item) => {
        item.getAttribute("small") ? smallCount++ : false;
      });

      this.promoFlagSize = smallCount === slottedPromoFlags.length ? "small" : "normal";
    }
  }

  getElemenInContainerPosition(element, container) {
    let elementPos = element.getBoundingClientRect();
    let containerPos = container.getBoundingClientRect();
    let relativePos = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      inView: false,
      outOfViewTop: false,
      outOfViewRight: false,
      outOfViewBottom: false,
      outOfViewLeft: false
    };

    relativePos.top = elementPos.top - containerPos.top;
    relativePos.right = elementPos.right - containerPos.right;
    relativePos.bottom = elementPos.bottom - containerPos.bottom;
    relativePos.left = elementPos.left - containerPos.left;
    relativePos.inView =
      relativePos.top >= 0 && relativePos.left >= 0 && relativePos.bottom <= 0 && relativePos.right <= 0;
    relativePos.outOfViewTop = relativePos.top < 0 ? true : false;
    relativePos.outOfViewLeft = relativePos.left < 0 ? true : false;
    relativePos.outOfViewBottom = relativePos.bottom > 0 ? true : false;
    relativePos.outOfViewRight = relativePos.right > 0 ? true : false;

    return relativePos;
  }

  @Watch("autoSlider")
  handleAutoSlider() {
    if (this.autoSlider) {
      this.internalAutoSlider = true;
      this.autoslider = window.setInterval(() => {
        this.activeStageIndex < this.stagesCount - 1
          ? (this.activeStageIndex = this.activeStageIndex + 1)
          : (this.activeStageIndex = 0);
        this.scrollItemIntoView(this.activeStageIndex);
      }, this.sliderTimer);
    } else {
      setTimeout(() => {
        this.internalAutoSlider = false;
      }, 500);
      this.initialTimeout = clearTimeout(this.initialTimeout);
    }
  }

  @Listen("slideDragged")
  slideChangedHandler(event) {
    if (event.srcElement !== this.hostElement) {
      return;
    }

    // as only one slide is visible on mobile, this should be selected as soon as the user interacts.
    if (isMobile()) {
      this.activeStageIndex = event.detail.currentItemIndex;
      clearInterval(this.autoslider);
      this.autoSlider = false;
    }
  }

  @Listen("resize", { target: "window" })
  resizeHandler() {
    const oldScreenSize = this.currentScreenSize;
    this.currentScreenSize = window.innerWidth;

    if (oldScreenSize === this.currentScreenSize) {
      return;
    }

    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.resizeTimeout = null;
        this.setViewportSpecificSettings();
        this.setContentWrapperHeight();
      }, this.resizeThrottleMs);
    }
  }

  setViewportSpecificSettings() {
    this.wrapperOversize = false;

    if (isMobile()) {
      this.slidesPerView = 1;
      this.showControls = false;
    } else if (isTablet()) {
      this.slidesPerView = this.stagesCount < 2 ? this.stagesCount : 2;
      this.showControls = true;
    } else if (window.innerWidth > (1920 / 4) * 5) {
      this.slidesPerView = this.stagesCount < 5 ? this.stagesCount : 5;
      this.wrapperOversize = true;
      this.showControls = true;
    } else {
      this.slidesPerView = this.stagesCount < 4 ? this.stagesCount : 4;
      this.showControls = true;
    }
  }

  async scrollItemIntoView(index) {
    const carouselState = await this.carousel.getCarouselState();
    if (index < carouselState.firstVisibleItemIndex) this.carousel.goToSlide(index);
    if (index > carouselState.lastVisibleItemIndex) this.carousel.goToSlide(index - this.slidesPerView + 1);
  }

  private handleClick(index) {
    this.ignoreNextFocus = true;
    this.scrollItemIntoView(index);
    this.activateItem(index);
    this.mcsItemClick.emit(this.activeStageIndex);
  }

  private activateItem(index) {
    this.fastForwardAnimation = true;
    this.activeStageIndex = index;
    clearInterval(this.autoslider);
    this.autoSlider = false;
    (this.hostElement.shadowRoot.querySelectorAll(".eonui-mcs-tab")[this.activeStageIndex] as HTMLElement).focus({
      preventScroll: true
    });
  }

  setContentWrapperHeight() {
    const contentWrapperHeight = (this.stages[this.activeStageIndex] as HTMLElement)?.offsetHeight;

    if (this.contentWrapperInner && contentWrapperHeight) {
      this.contentWrapperInner.style.height = `${contentWrapperHeight}px`;
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.HOME:
        e.preventDefault();
        this.fastForwardAnimation = true;
        this.activeStageIndex = 0;
        (this.hostElement.shadowRoot.querySelectorAll(".eonui-mcs-tab")[this.activeStageIndex] as HTMLElement).focus({
          preventScroll: true
        });
        clearInterval(this.autoslider);
        this.autoSlider = false;
        this.scrollItemIntoView(this.activeStageIndex);
        break;
      case KeyIds.END:
        e.preventDefault();
        this.fastForwardAnimation = true;
        this.activeStageIndex = this.stagesCount - 1;
        (this.hostElement.shadowRoot.querySelectorAll(".eonui-mcs-tab")[this.activeStageIndex] as HTMLElement).focus({
          preventScroll: true
        });
        clearInterval(this.autoslider);
        this.autoSlider = false;
        this.scrollItemIntoView(this.activeStageIndex);
        break;
      case KeyIds.RIGHT:
        e.preventDefault();
        this.fastForwardAnimation = true;
        this.activeStageIndex < this.stagesCount - 1
          ? (this.activeStageIndex = this.activeStageIndex + 1)
          : (this.activeStageIndex = 0);
        (this.hostElement.shadowRoot.querySelectorAll(".eonui-mcs-tab")[this.activeStageIndex] as HTMLElement).focus({
          preventScroll: true
        });
        clearInterval(this.autoslider);
        this.autoSlider = false;
        this.scrollItemIntoView(this.activeStageIndex);
        break;
      case KeyIds.LEFT:
        e.preventDefault();
        this.fastForwardAnimation = true;
        this.activeStageIndex > 0
          ? (this.activeStageIndex = this.activeStageIndex - 1)
          : (this.activeStageIndex = this.stagesCount - 1);
        (this.hostElement.shadowRoot.querySelectorAll(".eonui-mcs-tab")[this.activeStageIndex] as HTMLElement).focus({
          preventScroll: true
        });
        clearInterval(this.autoslider);
        this.autoSlider = false;
        this.scrollItemIntoView(this.activeStageIndex);
        break;
      default:
        break;
    }
  }

  private stageWithoutImage() {
    let img;
    this.stages.map((stage: Element) => {
      img = !!stage.querySelector('[slot="stage-item-image"]');
    });
    this.noImage = !img;
  }

  private handleTabFocus(index: number) {
    if (!this.ignoreNextFocus) {
      this.activeTabIndex = index;
    } else {
      this.ignoreNextFocus = false;
    }
    this.scrollItemIntoView(index);
    this.activateItem(index);
    this.mcsItemClick.emit(this.activeStageIndex);
  }

  private handleTabBlur(_index: number) {
    this.activeTabIndex = -1;
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            mcs: true,
            "with-text-on-background": this.isTextOnBackground,
            "bottom-alignment": this.bottomAlignment
          })}
          tabIndex={0}
          onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
          role="button"
        >
          <div class={pfx("mcs")}>
            <div class={pfx("mcs-tablist")} role="tablist">
              {this.stages.map((stage: Element, index: number) => {
                return (
                  <button
                    role="tab"
                    id={`${this.tabIdPrefix}${index}-tab`}
                    aria-selected={index === this.activeStageIndex ? "true" : "false"}
                    aria-controls={`${this.tabIdPrefix}${index}-tabpanel`}
                    aria-labelledby={`${this.tabIdPrefix}${index}-label`}
                    aria-describedby={`${this.tabIdPrefix}${index}-desc`}
                    tabindex={index === this.activeStageIndex ? "0" : "-1"}
                    onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
                    onFocus={() => this.handleTabFocus(index)}
                    onBlur={() => this.handleTabBlur(index)}
                    class={pfx("mcs-tab")}
                  >
                    {stage.getAttribute("headline")}
                  </button>
                );
              })}
            </div>
            <div class={pfx("mcs-content-group")}>
              <div
                class={pfx({
                  "mcs-wrapper": true,
                  oversize: this.wrapperOversize,
                  [`has-promo-flag-${this.promoFlagSize}`]: this.promoFlagSize,
                  "reduce-height": this.noImage && this.reduceHeaderHeight
                })}
              >
                <div class="mcs-slots-wrapper">
                  <slot name="heading"></slot>
                  <slot name="subheading"></slot>
                  <slot name="button-wrapper"></slot>
                </div>

                <div class={pfx("mcs-background")} style={{ height: `${this.backgroundSize}px` }}>
                  {!this.staticBackground &&
                    !this.backgroundColor &&
                    this.stages.map((stage: Element, index: number) => {
                      return (
                        <div
                          class={pfx({
                            "mcs-background-item": true,
                            active: index === this.activeStageIndex,
                            blurred: isFirefox() && this.blurBackground // fix for firefox that does not support backdrop-filter blur
                          })}
                          innerHTML={stage.querySelector('[slot="stage-item-background"]')?.outerHTML}
                        ></div>
                      );
                    })}
                  {!!this.staticBackground && !this.backgroundColor && (
                    <div
                      class={pfx({
                        "mcs-background-item": true,
                        active: true,
                        blurred: isFirefox() && this.blurBackground // fix for firefox that does not support backdrop-filter blur
                      })}
                      innerHTML={this.staticBackground.outerHTML}
                    ></div>
                  )}
                  {this.backgroundColor && (
                    <div
                      class={pfx("mcs-background-color")}
                      style={{ background: `var(--eon-${this.backgroundColor})` }}
                    ></div>
                  )}
                  {this.blurBackground && <div class={pfx("mcs-background-blur")}></div>}
                </div>
                <div
                  class={pfx("mcs-stages-wrapper-frame")}
                  style={{
                    ...(this.bottomAlignment && { bottom: `-${this.mscItemPositionBottom}px` })
                  }}
                  ref={(el) => (this.wrapper = el as HTMLElement)}
                >
                  <div
                    class={pfx({
                      "mcs-stages-wrapper": true,
                      "is-slider": this.isSlider,
                      autoslider: this.internalAutoSlider,
                      [`stages-${this.stagesCount}`]: true,
                      "no-blur": !this.blurBackground,
                      "no-blur-ff": !this.blurBackground && isFirefox()
                    })}
                    ref={(el) => (this.stagesWrapper = el as HTMLElement)}
                    aria-hidden="true"
                  >
                    <eon-ui-carousel
                      ref={(el) => (this.carousel = el as HTMLEonUiCarouselElement)}
                      controlStyle="default"
                      controls={this.showControls}
                      darkenControls={true}
                      loop={false}
                      setIndicators={false}
                      fullWidth={true}
                      itemsPerSlide={this.slidesPerView}
                      sameHeight={true}
                      gap="16px"
                      arrowFocus={false}
                    >
                      {this.stages.map((stage: Element, index: number) => {
                        const img = !!stage.querySelector('[slot="stage-item-image"]');
                        return (
                          <eon-ui-carousel-item>
                            {/* Element is not focusable, so no need to add a keyDown listener;
                          We should listen for a user interaction even though element is not interactable (because the content will be) */}
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */}{" "}
                            <div
                              aria-hidden="true"
                              class={pfx({
                                "mcs-item": true,
                                "no-image": !img,
                                active: index === this.activeStageIndex,
                                "focus-visible": this.activeTabIndex === index && this.activeStageIndex === index
                              })}
                              onClick={() => this.handleClick(index)}
                            >
                              {img && (
                                <div class={pfx("mcs-item-image")}>
                                  <div class={pfx("mcs-item-image-inner")}>
                                    <div class={pfx("mcs-loader")}>
                                      <div
                                        class={pfx({
                                          "mcs-loader-element": true,
                                          "fast-forward": this.fastForwardAnimation
                                        })}
                                      ></div>
                                    </div>
                                    <div innerHTML={stage.querySelector('[slot="stage-item-image"]')?.outerHTML}></div>
                                  </div>
                                </div>
                              )}
                              <div class={pfx("mcs-item-description")}>
                                <div class={pfx("mcs-item-description-background")}>
                                  {!img && (
                                    <div class={pfx("mcs-loader")}>
                                      <div
                                        class={pfx({
                                          "mcs-loader-element": true,
                                          "fast-forward": this.fastForwardAnimation
                                        })}
                                      ></div>
                                    </div>
                                  )}
                                </div>
                                <div class={pfx("mcs-item-description-inner")}>
                                  <div class={pfx("mcs-item-headline")} id={`${this.tabIdPrefix}${index}-label`}>
                                    {stage.getAttribute("icon") && (
                                      <eon-ui-icon
                                        class={pfx("icon")}
                                        size="small"
                                        name={stage.getAttribute("icon")}
                                        aria-hidden="true"
                                      ></eon-ui-icon>
                                    )}
                                    {stage.getAttribute("headline")}
                                  </div>
                                  <div class={pfx("mcs-item-text")} id={`${this.tabIdPrefix}${index}-desc`}>
                                    {stage.getAttribute("text")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </eon-ui-carousel-item>
                        );
                      })}
                    </eon-ui-carousel>
                  </div>
                </div>
              </div>
              {/* Element is not focusable, so no need to add a keyDown listener;
            We should listen for a user interaction even though element is not interactable (because the content will be) */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
              <div
                class={pfx("mcs-content-wrapper")}
                ref={(el) => (this.contentWrapper = el as HTMLElement)}
                onClick={() => {
                  if (this.autoSlider) this.handleClick(this.activeStageIndex);
                }}
              >
                <div
                  role="tabpanel"
                  id={`${this.tabIdPrefix}${this.activeStageIndex}-panel`}
                  aria-labelledby={`${this.tabIdPrefix}${this.activeStageIndex}-tab`}
                  tabindex={"0"}
                  class={pfx({
                    "mcs-content-wrapper-inner": true,
                    "overflow-hidden": this.overflowHidden
                  })}
                  ref={(el) => (this.contentWrapperInner = el as HTMLElement)}
                >
                  <slot />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
